/* eslint-disable react-hooks/exhaustive-deps */
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React from 'react';
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { getUniqueOptions } from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { useGetCausesList } from '@/modules/periodicTasks/apihooks';
import {
  useCreateEditCustomInput,
  useGetActiveSubstances,
  useGetEntitiesCountries,
} from '@/modules/settings/company/apiHooks';
import { useCompanySettingsUtils } from '@/modules/settings/company/components/logic';
import { ICompanySettings } from '@/modules/settings/company/types';
import { useGetWarehouseCategories } from '@/modules/settings/property/apiHooks';
import {
  Button,
  CircularBgImage,
  InputField,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import ScrollableWithShadow from '@/shared/components/atoms/ScrollableWithShadow';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import SwitchButton from '@/shared/components/atoms/SwitchButton';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { measurementUnits } from '@/shared/constant/general';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { useDidUpdateEffect } from '@/shared/hooks/useDidUpdateEffect';
import { getCountryIcon } from '@/shared/lib/country-flags';
import { IGeneral } from '@/shared/types';

import Plus from '~/images/shared/Plus.svg';
import Trash from '~/images/shared/Trash.svg';

const CreateEditInput: React.FC<{
  input?: ICompanySettings.ICompanyInput;
  full?: boolean;
}> = ({ input, full = true }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { createInputValidationSchema } = useCompanySettingsUtils();

  //************************* Form *************************

  const defaultValues: Partial<ICompanySettings.ICreateInputFormValues> = input
    ? {
        categoryId: input.categoryId,
        manufacturer: input.manufacturer,
        measurementType: input.measurementType,
        causeIds: input?.causes?.map((cause) => cause.id),
        name: input.name,
        license: input.license,
        countryId: input.country.id,
        hasActiveSubstances: !!input.activeSubstances?.length,
        activeSubstances: input.activeSubstances?.map((substance) => ({
          id: substance.id as number,
          concentration: substance.concentration
            ? substance.concentration
            : null,
        })),
      }
    : {
        hasActiveSubstances: true,
        activeSubstances: [
          {
            id: null,
            concentration: null,
          },
        ],
      };

  const formMethods = useForm<ICompanySettings.ICreateInputFormValues>({
    defaultValues,
    resolver: yupResolver(createInputValidationSchema),
  });
  const {
    register,
    formState: { errors },
    watch,
    control,
    setValue,
    resetField,
  } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'activeSubstances',
  });

  //************************* Form watchers *************************

  const countryId = watch('countryId');
  const hasActiveSubstances = watch('hasActiveSubstances');
  const activeSubstancesWatcher = watch('activeSubstances');

  //************************* APIs *************************

  const { data: countries } = useGetEntitiesCountries(user?.companyId);
  const { data: categories } = useGetWarehouseCategories(countryId);
  const { data: causes } = useGetCausesList({ countryId, paginated: false });
  const { data: activeSubstancesPages, fetchNextPage } = useGetActiveSubstances(
    {
      countryId,
      page: 1,
      limit: 50,
      activeSubstanceIds: input?.activeSubstances
        ?.map((item) => item.id)
        .join(','),
    }
  );
  const { mutate, isLoading } = useCreateEditCustomInput();

  // Get unique active substances select field options
  const getActiveSubstancesList = (selectedId?: number | null) => {
    const activeSubstances = activeSubstancesPages?.pages.flatMap(
      (page) => page.items
    );
    return activeSubstances
      ? getUniqueOptions<IGeneral.NameIdPair>({
          allOptions: activeSubstances,
          selectedOptions: activeSubstancesWatcher?.map((substance) => ({
            id: substance.id as number,
            name: '',
          })),
          getValue: (option) => option?.id,
          exclude: activeSubstances.find(
            (substance) => substance.id === selectedId
          ),
        })
      : [];
  };

  // submit handler
  const onSubmit: SubmitHandler<ICompanySettings.ICreateInputFormValues> = (
    values
  ) => {
    const { hasActiveSubstances: _, ...obj } = values;
    mutate(
      { obj: obj as ICompanySettings.ICreateInputDTO, id: input?.productId },
      {
        onSuccess: () => {
          if (!full) closeModal();
          openModal(
            <ConfirmationModal
              header={t(input ? 'edit-input-success' : 'create-input-success')}
              variant='success'
              onDismissRemoveNumber={full ? 2 : 1}
              autoDismissAfter={5000}
            />,
            { dismissable: false }
          );
        },
      }
    );
  };

  // reset active substances
  useDidUpdateEffect(() => {
    setValue(
      'activeSubstances',
      hasActiveSubstances ? [{ id: null, concentration: null }] : undefined
    );
  }, [hasActiveSubstances]);

  useDidUpdateEffect(() => {
    resetField('categoryId', { defaultValue: undefined });
    resetField('causeIds', { defaultValue: undefined });
    setValue(
      'activeSubstances',
      hasActiveSubstances ? [{ id: null, concentration: null }] : undefined
    );
  }, [countryId]);

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(onSubmit)}
        className={classNames(
          'mx-auto flex h-full flex-col justify-between',
          full
            ? 'items-center pt-40 pb-80 tablet:max-w-2xl'
            : 'items-end desktop:min-w-[660px]'
        )}
      >
        <ScrollableWithShadow className='w-full'>
          {full && (
            <Typography variant='h1' weight='semiBold' className='mb-40'>
              {t(input ? 'edit-custom-input' : 'create-new-custom-input')}
            </Typography>
          )}
          <div className='grid grid-cols-2 gap-16'>
            <InputField
              py='!py-14'
              id='name'
              {...register('name', {
                setValueAs: (value) => (value?.length ? value : undefined),
              })}
              width='col-span-2'
              label={t('product-name')}
              placeholder={t('product-name')}
              error={!!errors.name}
              errorMessage={errors.name?.message}
            />
            <SelectField
              id='countryId'
              py='0.5rem'
              px='0.5rem'
              borderRadius='1rem'
              borderColor={themeColors().outline.variant}
              isDisabled={!!input}
              getLabel={(obj: ICompanySettings.IEntityCountry) => obj.fullName}
              getValue={(obj: ICompanySettings.IEntityCountry) => obj.id}
              formatOptionLabel={(option: ICompanySettings.IEntityCountry) => (
                <div className='flex items-center gap-4'>
                  <CircularBgImage
                    size='md'
                    imageSrc={getCountryIcon(option.name)}
                  />
                  {option.fullName}
                </div>
              )}
              label={t('country')}
              placeholder={t('country')}
              list={countries}
            />
            <SelectField
              py='0.5rem'
              px='0.5rem'
              borderRadius='1rem'
              borderColor={themeColors().outline.variant}
              id='categoryId'
              getLabel={(obj: IGeneral.NameIdPair) => obj.name}
              getValue={(obj: IGeneral.NameIdPair) => obj.id}
              isDisabled={!countryId || !!input?.used}
              label={t('item-category')}
              placeholder={t('item-category')}
              list={categories?.items}
            />
            <SelectField
              py='0.5rem'
              px='0.5rem'
              borderRadius='1rem'
              borderColor={themeColors().outline.variant}
              id='measurementType'
              isDisabled={!!input?.used}
              getLabel={(obj: IGeneral.LabelValuePair) => obj.label}
              getValue={(obj: IGeneral.LabelValuePair) => obj.value}
              label={t('measurement-unit')}
              placeholder={t('measurement-unit')}
              list={measurementUnits.map((unit) => ({
                label: t(unit.toLowerCase()),
                value: unit,
              }))}
            />
            <InputField
              py='!py-14'
              id='manufacturer'
              {...register('manufacturer', {
                setValueAs: (value) => (value?.length ? value : undefined),
              })}
              label={t('manufacturer')}
              placeholder={t('manufacturer')}
              error={!!errors.manufacturer}
              errorMessage={errors.manufacturer?.message}
            />
            <InputField
              py='!py-14'
              id='license'
              {...register('license', {
                setValueAs: (value) => (value?.length ? value : undefined),
              })}
              label={`${t('license-number')} (${t('optional')})`}
              placeholder={t('license-number')}
              error={!!errors.license}
              errorMessage={errors.license?.message}
            />
            <SelectField
              py='0.5rem'
              px='0.5rem'
              borderRadius='1rem'
              borderColor={themeColors().outline.variant}
              id='causeIds'
              isMulti
              getLabel={(obj: IGeneral.NameIdPair) => obj.name}
              getValue={(obj: IGeneral.NameIdPair) => obj.id}
              label={`${t('purpose')} (${t('optional')})`}
              isDisabled={!countryId}
              placeholder={t('purpose')}
              list={causes?.items}
            />
          </div>
          <div className='mt-40'>
            <div className='mb-24 flex items-center gap-8'>
              <SwitchButton describe={false} id='hasActiveSubstances' />
              <Typography weight='medium'>{t('active-substances')}</Typography>
            </div>
            {hasActiveSubstances && (
              <>
                <div className='grid grid-cols-2 gap-16'>
                  {fields.map((field, index) => (
                    <React.Fragment key={field.id}>
                      <InfiniteSelectField
                        py='0.5rem'
                        px='0.5rem'
                        borderRadius='1rem'
                        borderColor={themeColors().outline.variant}
                        width='w-full'
                        id={`activeSubstances.${index}.id`}
                        getLabel={(obj: IGeneral.NameIdPair) => obj.name}
                        getValue={(obj: IGeneral.NameIdPair) => obj.id}
                        label={index === 0 ? t('active-substance') : undefined}
                        placeholder={t('active-substance')}
                        fetchNextPage={fetchNextPage}
                        list={getActiveSubstancesList(
                          activeSubstancesWatcher?.[index]?.id
                        )}
                        isDisabled={!countryId}
                      />
                      <div>
                        <div className='flex items-center gap-8'>
                          <InputField
                            py='!py-14'
                            width='w-full'
                            {...register(
                              `activeSubstances.${index}.concentration`,
                              {
                                setValueAs: (value) => Number(value),
                              }
                            )}
                            type='number'
                            step={0.01}
                            label={index === 0 ? t('concentration') : undefined}
                            placeholder={t('concentration')}
                            error={
                              !!errors?.activeSubstances?.[index]?.concentration
                            }
                            errorMessage={
                              errors?.activeSubstances?.[index]?.concentration
                                ?.message
                            }
                          />
                          {index > 0 && (
                            <span
                              className='cursor-pointer'
                              onClick={() => remove(index)}
                            >
                              <Trash />
                            </span>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div
                  className='my-24 flex cursor-pointer items-center gap-8'
                  onClick={() => append({ id: null, concentration: null })}
                >
                  <span className='flex h-16 w-16 items-center justify-center rounded-full bg-secondary-90 text-green-300'>
                    <Plus width='10' height='10' />
                  </span>
                  <Typography weight='medium'>
                    {t('add-active-substance')}
                  </Typography>
                </div>
              </>
            )}
          </div>
        </ScrollableWithShadow>
        <Button
          disabled={isLoading}
          loading={isLoading}
          type='submit'
          className='h-56 !rounded-xl'
          size={full ? 'full' : 'lg'}
        >
          {t('save')}
        </Button>
      </form>
    </FormProvider>
  );
};

export default CreateEditInput;
